import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import KitchenForm from "../components/KitchenForm";
import { exportFullList, exportKitchenCard } from "../lib/pdf";
import {
  ChefHat, Plus, Search, FileDown, LogOut, Pencil, Trash2, FileText, Inbox,
} from "lucide-react";

const STATO_STYLE = {
  "In sviluppo": "bg-[#FEF3C7] text-[#92400E]",
  "Definita": "bg-[#E0F2FE] text-[#075985]",
  "Esposta": "bg-[#D1FAE5] text-[#065F46]",
};

const COLS = [
  ["azienda", "Azienda"], ["rivenditore", "Rivenditore"], ["agente", "Agente"],
  ["modello", "Modello"], ["finitura", "Finitura"], ["tipologia", "Tipologia"],
  ["modello_living", "Living"], ["data_sviluppata", "Sviluppata"],
  ["data_definita", "Definita"], ["prende_posto_di", "Sostituisce"],
];

function StatBox({ label, value, testid }) {
  return (
    <div className="px-6 py-6 border-r border-neutral-200 last:border-r-0" data-testid={testid}>
      <p className="font-head text-5xl font-light tracking-tight text-neutral-900 tabular-nums">{value}</p>
      <p className="text-xs uppercase tracking-[0.15em] text-neutral-400 mt-2">{label}</p>
    </div>
  );
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [kitchens, setKitchens] = useState([]);
  const [stats, setStats] = useState({ total: 0, esposta: 0, in_sviluppo: 0, definita: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStato, setFilterStato] = useState("all");
  const [filterTipo, setFilterTipo] = useState("all");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = async () => {
    try {
      const [k, s] = await Promise.all([api.get("/kitchens"), api.get("/kitchens/stats")]);
      setKitchens(k.data);
      setStats(s.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return kitchens.filter((k) => {
      const matchQ = !q || [k.azienda, k.rivenditore, k.agente, k.modello, k.finitura, k.modello_living]
        .some((v) => (v || "").toLowerCase().includes(q));
      const matchStato = filterStato === "all" || k.stato === filterStato;
      const matchTipo = filterTipo === "all" || k.tipologia === filterTipo;
      return matchQ && matchStato && matchTipo;
    });
  }, [kitchens, search, filterStato, filterTipo]);

  const handleSave = async (payload, id) => {
    setSaving(true);
    try {
      if (id) {
        await api.put(`/kitchens/${id}`, payload);
        toast.success("Cucina aggiornata");
      } else {
        await api.post("/kitchens", payload);
        toast.success("Cucina aggiunta");
      }
      setFormOpen(false);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/kitchens/${deleteTarget.id}`);
      toast.success("Cucina eliminata");
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const doExportList = () => {
    if (filtered.length === 0) { toast.error("Nessuna cucina da esportare"); return; }
    exportFullList(filtered);
    toast.success("PDF elenco generato");
  };

  return (
    <div className="min-h-screen bg-[#F9F9F8]">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-xl border-b border-neutral-200">
        <div className="max-w-[1600px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ChefHat strokeWidth={1.5} className="h-6 w-6 text-[#B4654A]" />
            <span className="font-head text-lg tracking-tight text-neutral-900">Cucine Expo</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-neutral-500 hidden sm:block">{user?.email}</span>
            <Button data-testid="logout-button" variant="ghost" size="sm" onClick={logout} className="rounded-none text-neutral-600 hover:text-neutral-900">
              <LogOut className="h-4 w-4 mr-1.5" /> Esci
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-6 py-10">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-neutral-400">Gestionale esposizioni</p>
            <h1 className="font-head text-4xl font-light tracking-tight text-neutral-900 mt-1">Le tue cucine</h1>
          </div>
          <div className="flex gap-3">
            <Button data-testid="export-list-button" variant="outline" onClick={doExportList} className="rounded-none border-neutral-300 hover:bg-neutral-100">
              <FileDown className="h-4 w-4 mr-2" /> Esporta PDF
            </Button>
            <Button data-testid="add-kitchen-button" onClick={() => { setEditing(null); setFormOpen(true); }} className="rounded-none bg-[#B4654A] hover:bg-[#98553E] text-white">
              <Plus className="h-4 w-4 mr-2" /> Nuova cucina
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 bg-white border border-neutral-200 mb-8">
          <StatBox label="Totale cucine" value={stats.total} testid="stat-total" />
          <StatBox label="In sviluppo" value={stats.in_sviluppo} testid="stat-sviluppo" />
          <StatBox label="Definite" value={stats.definita} testid="stat-definita" />
          <StatBox label="Esposte" value={stats.esposta} testid="stat-esposta" />
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
            <Input
              data-testid="search-input"
              placeholder="Cerca azienda, rivenditore, modello…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="rounded-none border-neutral-200 focus-visible:ring-0 focus-visible:border-neutral-900 h-10 pl-10 bg-white"
            />
          </div>
          <Select value={filterStato} onValueChange={setFilterStato}>
            <SelectTrigger data-testid="filter-stato" className="rounded-none border-neutral-200 h-10 w-full sm:w-44 bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-none bg-white border-neutral-200">
              <SelectItem value="all" className="rounded-none">Tutti gli stati</SelectItem>
              <SelectItem value="In sviluppo" className="rounded-none">In sviluppo</SelectItem>
              <SelectItem value="Definita" className="rounded-none">Definita</SelectItem>
              <SelectItem value="Esposta" className="rounded-none">Esposta</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterTipo} onValueChange={setFilterTipo}>
            <SelectTrigger data-testid="filter-tipo" className="rounded-none border-neutral-200 h-10 w-full sm:w-44 bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-none bg-white border-neutral-200">
              <SelectItem value="all" className="rounded-none">Tutte le tipologie</SelectItem>
              {["Lineare", "Angolare", "Penisola", "Isola"].map((t) => (
                <SelectItem key={t} value={t} className="rounded-none">{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <div className="bg-white border border-neutral-200">
          <div className="table-scroll overflow-x-auto">
            <table className="w-full text-sm" data-testid="kitchens-table">
              <thead>
                <tr className="border-b border-neutral-200">
                  {COLS.map(([, label]) => (
                    <th key={label} className="text-left font-medium text-[11px] uppercase tracking-[0.1em] text-neutral-400 px-4 py-3 whitespace-nowrap">{label}</th>
                  ))}
                  <th className="text-left font-medium text-[11px] uppercase tracking-[0.1em] text-neutral-400 px-4 py-3">Stato</th>
                  <th className="text-right font-medium text-[11px] uppercase tracking-[0.1em] text-neutral-400 px-4 py-3">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={COLS.length + 2} className="px-4 py-16 text-center text-neutral-400">Caricamento…</td></tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={COLS.length + 2} className="px-4 py-20 text-center">
                      <Inbox className="h-10 w-10 mx-auto text-neutral-300 mb-3" strokeWidth={1.2} />
                      <p className="text-neutral-500">Nessuna cucina trovata</p>
                      <p className="text-sm text-neutral-400 mt-1">Aggiungi la tua prima esposizione.</p>
                    </td>
                  </tr>
                ) : (
                  filtered.map((k) => (
                    <tr key={k.id} className="border-b border-neutral-100 hover:bg-neutral-50 transition-colors" data-testid={`kitchen-row-${k.id}`}>
                      {COLS.map(([key]) => (
                        <td key={key} className="px-4 py-3 whitespace-nowrap text-neutral-700 tabular-nums">{k[key] || <span className="text-neutral-300">—</span>}</td>
                      ))}
                      <td className="px-4 py-3">
                        <span className={`inline-block px-2.5 py-1 text-[11px] font-medium ${STATO_STYLE[k.stato] || "bg-neutral-100 text-neutral-600"}`}>{k.stato}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button data-testid={`export-card-${k.id}`} onClick={() => { exportKitchenCard(k); toast.success("Scheda PDF generata"); }} title="Esporta scheda" className="p-1.5 text-neutral-400 hover:text-[#B4654A] transition-colors">
                            <FileText className="h-4 w-4" />
                          </button>
                          <button data-testid={`edit-${k.id}`} onClick={() => { setEditing(k); setFormOpen(true); }} title="Modifica" className="p-1.5 text-neutral-400 hover:text-neutral-900 transition-colors">
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button data-testid={`delete-${k.id}`} onClick={() => setDeleteTarget(k)} title="Elimina" className="p-1.5 text-neutral-400 hover:text-red-600 transition-colors">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      <KitchenForm open={formOpen} onOpenChange={setFormOpen} initial={editing} onSave={handleSave} saving={saving} />

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-none bg-white border-neutral-200">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-head font-light">Eliminare la cucina?</AlertDialogTitle>
            <AlertDialogDescription>
              Stai per eliminare "{deleteTarget?.modello || "questa cucina"}". L'azione è irreversibile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-button" onClick={handleDelete} className="rounded-none bg-red-600 hover:bg-red-700 text-white">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
